import {
  ArrowRight,
  CreditCard,
  GearSix,
  Sparkle,
  Storefront,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import type { ReactNode } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { BusinessMembership } from "../inbox/types";
import { WorkspaceLink } from "./WorkspaceLink";

type SetupSummary = {
  profile: string | null;
  onboardingStatus: "NOT_CONFIGURED" | "CONFIGURED";
  counts: Record<string, number>;
};

type ConnectionHealth = { status: string };

type CapabilityReadiness = {
  readinessStatus: "READY" | "ACTION_REQUIRED";
};

export function WorkspaceSetup({ business }: { business: BusinessMembership }) {
  const businessId = business.businessId as Id<"businesses">;
  const setupSummary = useQuery(api.businessSetup.getSetupSummary, {
    businessId,
  }) as SetupSummary | undefined;
  const connectionHealth = useQuery(
    api.whatsAppConnectionSetup.getConnectionHealth,
    { businessId },
  ) as ConnectionHealth | undefined;
  const capabilityExperience = useQuery(
    api.businessConfiguration.getInstallationExperience,
    { businessId },
  ) as CapabilityReadiness | undefined;

  if (
    setupSummary === undefined ||
    connectionHealth === undefined ||
    capabilityExperience === undefined
  ) {
    return (
      <div aria-busy="true" className="workspace-overview-state">
        <p role="status">Loading setup status...</p>
      </div>
    );
  }

  const owner = business.role === "OWNER";
  const capabilityStatus =
    capabilityExperience.readinessStatus === "READY"
      ? "Ready"
      : "Needs attention";

  return (
    <div className="workspace-setup">
      <header>
        <span className="workspace-eyebrow">Business setup</span>
        <h1>Business setup</h1>
        <p>
          Configure {business.name ?? "your business"} at your own pace. Your
          workspace stays available while setup is incomplete.
        </p>
      </header>
      <div className="setup-action-list">
        <SetupAction
          available={owner}
          description="Set the profile, services, resources, hours and supported hospitality details."
          href="/app/business-setup"
          icon={<Storefront weight="bold" />}
          label="Business details"
          status={setupStatus(setupSummary)}
        />
        <SetupAction
          available={owner}
          description="Connect and verify the WhatsApp number customers use."
          href="/app/whatsapp-connection"
          icon={<WhatsappLogo weight="bold" />}
          label="WhatsApp connection"
          status={connectionStatus(connectionHealth.status)}
        />
        <SetupAction
          available={owner}
          description="Enable the reusable capabilities this business needs."
          href="/app/capabilities"
          icon={<GearSix weight="bold" />}
          label="Capabilities"
          status={capabilityStatus}
        />
        <SetupAction
          available={owner}
          description="Draft a reviewable profile and capability recommendation."
          href="/app/configuration-assistant"
          icon={<Sparkle weight="bold" />}
          label="Configuration assistant"
          status="Optional"
        />
        <SetupAction
          available
          description="Review plan, account settings and usage visibility."
          href="/app/account"
          icon={<CreditCard weight="bold" />}
          label="Account settings"
          status="Available"
        />
      </div>
      {!owner ? (
        <p className="workspace-permission-note">
          Only an owner can change business setup. You can continue to the Inbox
          from the workspace navigation.
        </p>
      ) : null}
      <WorkspaceLink className="workspace-back-link" href="/app/inbox">
        Return to Inbox
        <ArrowRight weight="bold" />
      </WorkspaceLink>
    </div>
  );
}

function setupStatus(summary: SetupSummary) {
  if (summary.onboardingStatus === "CONFIGURED") return "Configured";
  const hasEvidence =
    summary.profile !== null ||
    Object.values(summary.counts).some((count) => count > 0);
  return hasEvidence ? "Partially configured" : "Not configured";
}

function connectionStatus(status: string) {
  if (status === "CONNECTED") return "Connected";
  if (status === "STARTED") return "Connection in progress";
  if (status === "VERIFICATION_PENDING" || status === "VERIFIED") {
    return "Verification in progress";
  }
  if (status === "CANCELLED") return "Setup cancelled";
  if (status === "NOT_STARTED") return "Not connected";
  return "Status unavailable";
}

function SetupAction({
  available,
  description,
  href,
  icon,
  label,
  status,
}: {
  available: boolean;
  description: string;
  href: string;
  icon: ReactNode;
  label: string;
  status: string;
}) {
  const content = (
    <>
      <span aria-hidden="true" className="setup-action-icon">
        {icon}
      </span>
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <em>{available ? status : "Owner only"}</em>
    </>
  );

  return available ? (
    <WorkspaceLink className="setup-action" href={href}>
      {content}
    </WorkspaceLink>
  ) : (
    <div aria-disabled="true" className="setup-action is-disabled">
      {content}
    </div>
  );
}
